/**
 * web/src/hooks/mutations/useRejectGate.ts
 * Reject with required reason. Server enforces non-empty (RejectBody.reason
 * min length 1). Surfaces the server message via toastApiError.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { qk } from '../../lib/queryKeys';
import { toast } from 'sonner';
import { rejectGate } from '../../api/gates';
import { toastApiError } from '../../lib/api';

/** Variables for the reject mutation. */
export interface UseRejectGateVars {
  workflowId: string;
  stepIndex: number;
  reason: string;
}

/**
 * Rejects a waiting gate. Step status flips to 'rejected'; the workflow
 * remains paused so the reviewer can edit and resubmit.
 *
 * @returns React Query mutation handle
 * @sideEffects HTTP POST
 * @idempotent no — second call against a non-waiting step 409s
 */
export function useRejectGate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: UseRejectGateVars) =>
      rejectGate(vars.workflowId, vars.stepIndex, { reason: vars.reason }),
    onSuccess: (_data, vars) => {
      toast.success('Gate rejected — sent back to the queue for edit & resubmit');
      qc.invalidateQueries({ queryKey: qk.gates.pending() });
      qc.invalidateQueries({ queryKey: qk.workflow.root(vars.workflowId) });
      qc.invalidateQueries({ queryKey: qk.workflows.root() });
      qc.invalidateQueries({ queryKey: qk.stats.root() });
    },
    onError: (err) => {
      toastApiError(err, 'Reject failed');
    },
  });
}
